import gql from 'graphql-tag';

import { buildPaginationQueryParams } from './utils';

const decisionSheetCreditRiskAssessmentData = `
  backgroundCheckEmbargoStatus
  backgroundCheckForcedLaborStatus
  backgroundCheckNegativeDebtStatus
  coIssuerName
  contactAge
  contactCity
  contactName
  contactState
  currentRating
  guarantorName
  salesManager
  salesRep
  spouseName
  yearsWithIndigo
  yearsExperiencePlanting
`;

const cropCashFlows = `
  cropCashFlows {
    crop
    grossRevenue
    indigoProductivity
    leaseCost
    leasedLand
    ownedLand
    percentOwnedLand
    productionCost
    totalLand
  }
`;

const priorLoans = `
  priorLoans{
    campaignName
    collateralType
    tradingCompany
    usedLimit
    paymentStatus
    creditAssignmentStatus
    creditAssignmentValue
  }
`;
const decisionSheetQuery = `
  decisionSheet {
    ${decisionSheetCreditRiskAssessmentData}
    
    accountCity
    accountState
    assetValue
    ownedLand
    collateralArea
    ${cropCashFlows}
    
    additionalLimit
    approvedCreditAmount
    campaignName
    collateralTypes
    commitments
    currentLimit
    dueDate
    exposurePerCrop {
      exposure
      crop
    }
    exposureVsGrossRevenue
    grossRevenue
    totalExposure
    
    ${priorLoans}

    debts {
      city
      date
      informer
      situation
      state
      value
    }
    debtsCount
    debtsTotalValue
    lawsuits {
      caseNumber
      courtName
      lastUpdate
      polarity
      state
      status
      tags
      type
      value
    }
    lawsuitsCount
    lawsuitsTotalValue

    attentionPoints
    otherComments
    positivePoints
  }
`;

const creditApprovalStep = `
  id
  approvalType
  status
  decision
  decisionBy {
    id
    firstName
    lastName
  }
  decisionNotes
  createdAt
  updatedAt
`;

const creditApprovalDataQuery = `
  approvedCreditAmount
  round {
    id
    createdAt
    createdBy
    status
    creditThreshold {
      tier
    }
    steps {
      ${creditApprovalStep}
    }
    creditExceptions
    creditConditionals {
      id
      name
      validated
      observation
    }
  }
  ${decisionSheetQuery}
`;

export const getCreditApprovalData = gql`
  query getCreditApprovalData($agFinancingId: ID!) {
    creditApprovalData(agFinancingId: $agFinancingId) {
      ${creditApprovalDataQuery}
    }
  }
`;

const creditApprovalHistory = `
    financingAgFinancingQueue(pagination: $pagination, filters: $filters, sort: $sort) {
        ${buildPaginationQueryParams(`
            id
            collateralType
            campaign {
                name
            }
            orderInfo {
                opportunities {
                    id
                    contractValue
                    collateralBags
                    tradingName
                }
            }
            phase2 {
              status
            }
        `)}
    }
`;

const creditRiskAssessmentData = `
  creditRiskAssessmentData(opportunityId: $opportunityId, phase2Id: $phase2Id) {
    backgroundCheck {
        steps {
            type
            status
        }
    }
    spouseName
    guarantorName
    coIssuerName
    primaryContact {
        name
        address {
            adminArea
            city
            locality
            state
        }
    }
    ownerName
    managerName
    currentRating
    growerAge
    yearsIndigoRelationship
    yearsPlanting
  }
`;

// CPA data for Contact Details in the context of an Opportunity
// TODO: https://symbiota.atlassian.net/browse/MERCH-1842 to simplify this query
export const opportunityContactCreditOverviewData = gql`
  query opportunityContactCreditProposalAnalysisData($opportunityId: ID!, $phase2Id: ID, $pagination: AGFPagination!, $filters: AGFilters!, $sort: AGFSort = {id: "campaign", direction: ASC}, $primaryContactId: String!, $limit: Int, $offset: Int) {
    ${creditRiskAssessmentData}
    ${creditApprovalHistory}
    cropCashFlowData(primaryContactId: $primaryContactId, limit: $limit, offset: $offset) {
      cropCashFlows {
        crop
        ownedLand
        leasedLand
        totalLand
        percentOwnedLand
        productionCost {
          value
          code
        }
        leaseCost {
          value
          code
        }
        indigoProductivity
        grossRevenue
        notes {
          note
          createdAt
          user
        }
      }
      pageInfo {
        limit
        offset
        hasNextPage
        hasPreviousPage
      }
    }
  }
`;

export const createCreditApprovalRound = gql`
  mutation createCreditApprovalRound($creditApprovalRoundInput: CreditApprovalRoundInput!) {
    createCreditApprovalRound(creditApprovalRoundInput: $creditApprovalRoundInput) {
      ${creditApprovalDataQuery}
    }
  }
`;

export const submitCreditApprovalStepDecision = gql`
  mutation submitCreditApprovalStepDecision($creditApprovalStepInput: CreditApprovalStepInput!) {
    submitCreditApprovalStepDecision(creditApprovalStepInput: $creditApprovalStepInput) {
      ${creditApprovalStep}
    }
  }
`;

export const escalateCreditApprovalRound = gql`
  mutation escalateCreditApprovalRound($data: EscalateCreditApprovalRoundInput!) {
    escalateCreditApprovalRound(data: $data) {
      ${creditApprovalDataQuery}
    }
  }
`;

export const resetCreditApproval = gql`
  mutation resetCreditApproval($agFinancingId: ID!, $notes: String!) {
    resetCreditApproval(agFinancingId: $agFinancingId, notes: $notes) {
      id
    }
  }
`;

export const updateCreditApprovalRoundExceptions = gql`
  mutation updateCreditApprovalRoundExceptions(
    $creditApprovalRoundId: ID!, 
    $creditApprovalRoundExceptionsInput: UpdateCreditApprovalRoundExceptionsInput!
  ) {
    updateCreditApprovalRoundExceptions(
      creditApprovalRoundId: $creditApprovalRoundId, 
      creditApprovalRoundExceptionsInput: $creditApprovalRoundExceptionsInput
    ) {
      ${creditApprovalDataQuery}
    }
  }
`;

export const setApprovedCreditAmount = gql`
  mutation setApprovedCreditAmount($phase2Id: String!, $approvedCreditAmount: Float!) {
    setApprovedCreditAmount(phase2Id: $phase2Id, approvedCreditAmount: $approvedCreditAmount)
  }
`;

export const updateCreditApprovalRoundConditionals = gql`
  mutation updateCreditApprovalRoundConditionals(
    $creditApprovalRoundId: ID!, 
    $creditApprovalRoundConditionalsInput: UpdateCreditApprovalRoundConditionalsInput!
  ) {
    updateCreditApprovalRoundConditionals(
      creditApprovalRoundId: $creditApprovalRoundId, 
      creditApprovalRoundConditionalsInput: $creditApprovalRoundConditionalsInput
    ) {
      ${creditApprovalDataQuery}
    }
  }
`;

export const setApprovedConditionals = gql`
  mutation setApprovedConditionals($data: ApproveCreditConditionalInput!) {
    setApprovedConditionals(data: $data)
  }
`;

export const importExceptionsAndConditionals = gql`
  mutation importExceptionsAndConditionals($data: ImportExceptionsAndConditionalsInput!) {
    importExceptionsAndConditionals(data: $data) {
      id
      importedExceptions
      importedConditionals
      parentId
    }
  }
`;
